import ReactGA from 'react-ga4';

/**
 * Google Services Helper & Safeguards
 * Provides resilient initialization, telemetry tracking, and Crashlytics-compatible
 * error logging for Google Analytics 4, Google Tag Manager, Google AdSense, and Web Crashlytics.
 */
export const TAG_CONDITIONS = {
  HTTP: {
    name: 'http',
    googleTag: 'G-FT9PP7MDY8',
    gtagJsTag: 'G-FT9PP7MDY8',
    gtmTag: 'G-FT9PP7MDY8',
    googleAdsTag: 'AW-809044952',
  },
  HTTPS: {
    name: 'https',
    googleTag: 'G-JNW9ZSGHXH',
    gtagJsTag: 'G-JNW9ZSGHXH',
    gtmTag: 'G-JNW9ZSGHXH',
    googleAdsTag: 'AW-809044952',
  },
  CD: {
    name: 'cd',
    googleTag: 'G-XVLNFBSQMM',
    gtagJsTag: 'G-JNW9ZSGHXH',
    gtmTag: 'G-JNW9ZSGHXH',
    googleAdsTag: 'AW-809044952',
  },
  PCD: {
    name: 'pcd',
    googleTag: 'G-5CTTJBSDJ4',
    gtagJsTag: 'G-JNW9ZSGHXH',
    gtmTag: 'G-JNW9ZSGHXH',
    googleAdsTag: 'AW-809044952',
  },
};

export const FIREBASE_TAG = 'G-JNW9ZSGHXH';
export const GOOGLE_ADS_TAG = 'AW-809044952';

let isGaInitialized = false;
let isGtmInitialized = false;
let isAdSenseInitialized = false;
let isErrorHandlersInitialized = false;
let currentGoogleTag = null;
let currentGtmTag = null;
let currentCondition = null;

/**
 * Detect if the client is running in a mobile phone or handheld browser
 */
export const isMobileBrowser = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || navigator.vendor || window.opera || '';
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|HealthDash/i;
  const isSmallScreen = typeof window.innerWidth === 'number' && window.innerWidth <= 768;
  const hasTouch = 'ontouchstart' in window || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  return Boolean(mobileRegex.test(ua) || (isSmallScreen && hasTouch));
};

/**
 * Resolves active tracking condition and assigned tag matrix
 * Priority Hierarchy:
 * 1. PCD user and Pro/Premium UX (highest priority)
 * 2. CD user and UX (including Demo) (second priority)
 * 3. Protocol condition (HTTPS vs HTTP)
 *
 * For mixed cases:
 * GTM tag prioritizes CD/PCD (G-JNW9ZSGHXH) before falling back to protocol condition.
 */
export const resolveTrackingCondition = (context = {}) => {
  const isBrowser = typeof window !== 'undefined';
  const pathname = context.pathname || (isBrowser ? window.location.pathname : '/');
  const search = context.search || (isBrowser ? window.location.search : '');
  const protocol = context.protocol || (isBrowser ? window.location.protocol : 'https:');
  const user = context.user || null;
  const authState = context.authState || null;
  const isProActive = (isBrowser && Boolean(window.PCD_PRO_ACTIVE)) || Boolean(context.isProActive);
  const isCdActive = (isBrowser && (Boolean(window.CD_DEMO_ACTIVE) || Boolean(window.DEMO_MODE))) || Boolean(context.isCdActive);

  // Parse search query parameters
  const searchParams = new URLSearchParams(
    typeof search === 'string' && search.startsWith('?') ? search : (search ? `?${search}` : '')
  );
  const isDemoQuery = searchParams.get('demo') === 'true' || searchParams.get('demo') === '1';
  const isTierPro = searchParams.get('tier') === 'pro' || searchParams.get('tier') === 'premium';
  const isUxPcd = searchParams.get('ux') === 'pcd' || searchParams.get('ux') === 'pro' || searchParams.get('ux') === 'premium';

  // 1. PCD user and Pro/Premium UX check
  const isPcdRoute = pathname.startsWith('/ecosystem/PCD') || pathname.startsWith('/ecosystem/DSV');
  const hasProClaims = authState?.idToken?.claims?.subscription === 'Premium' ||
                       authState?.idToken?.claims?.tier === 'PRO' ||
                       authState?.idToken?.claims?.tier === 'PREMIUM';
  const isUserProTier = user?.tier === 'PRO' || user?.tier === 'PREMIUM';

  const isPcdCondition = Boolean(
    (isPcdRoute && !isDemoQuery) ||
    hasProClaims ||
    isUserProTier ||
    isProActive ||
    isTierPro ||
    isUxPcd
  );

  // 2. CD user and UX (including Demo) check
  const isCdRoute = pathname.startsWith('/ecosystem/CD');
  const isDemo = isDemoQuery || isCdActive;
  const isUserCdTier = user?.tier === 'CD' || user?.tier === 'STANDARD' || user?.tier === 'FOUNDATION';

  const isCdCondition = Boolean(
    isCdRoute ||
    isDemo ||
    isUserCdTier
  );

  // Apply Priority Order: PCD > CD > Protocol (HTTPS / HTTP)
  let conditionKey = 'HTTPS';
  if (isPcdCondition) {
    conditionKey = 'PCD';
  } else if (isCdCondition) {
    conditionKey = 'CD';
  } else if (protocol === 'http:') {
    conditionKey = 'HTTP';
  } else {
    conditionKey = 'HTTPS';
  }

  const baseConfig = TAG_CONDITIONS[conditionKey];

  // Mixed case GTM Tag resolution:
  // "For mixed cases, obviously, use Google Tag Manager tag that is related to CD/PCD and only then by secure/nonsecure and firebase condition."
  let resolvedGtmTag = baseConfig.gtmTag;
  if (isPcdCondition || isCdCondition) {
    resolvedGtmTag = TAG_CONDITIONS.PCD.gtmTag; // G-JNW9ZSGHXH
  } else if (protocol === 'http:') {
    resolvedGtmTag = TAG_CONDITIONS.HTTP.gtmTag; // G-FT9PP7MDY8
  } else {
    resolvedGtmTag = TAG_CONDITIONS.HTTPS.gtmTag; // G-JNW9ZSGHXH
  }

  return {
    condition: conditionKey,
    name: baseConfig.name,
    googleTag: baseConfig.googleTag,
    gtagJsTag: baseConfig.gtagJsTag,
    gtmTag: resolvedGtmTag,
    googleAdsTag: baseConfig.googleAdsTag,
    firebaseTag: FIREBASE_TAG,
    isMobile: isBrowser ? isMobileBrowser() : false,
    protocol,
    pathname,
  };
};

export const getGoogleConfig = (context = {}) => {
  const resolved = resolveTrackingCondition(context);
  const gaId = process.env.REACT_APP_GA_TRACKING_ID || resolved.googleTag;
  const adsId = process.env.REACT_APP_GADS_ID || resolved.googleAdsTag;
  const gtmId = process.env.REACT_APP_GTM_ID || resolved.gtmTag;
  const isExplicitlyDisabled = typeof window !== 'undefined' && Boolean(window.PCD_DISABLE_EXTENSIONS);
  const isProActive = (typeof window !== 'undefined' && Boolean(window.PCD_PRO_ACTIVE)) || Boolean(context.isProActive);

  // In production, when tracking is configured, and unless explicitly disabled
  const shouldInit = !isExplicitlyDisabled;

  return {
    gaId,
    adsId,
    gtmId,
    googleTag: resolved.googleTag,
    gtagJsTag: resolved.gtagJsTag,
    gtmTag: resolved.gtmTag,
    googleAdsTag: resolved.googleAdsTag,
    firebaseTag: FIREBASE_TAG,
    condition: resolved.condition,
    isMobile: resolved.isMobile,
    shouldInit,
    isProActive,
  };
};

/**
 * Initialize Google Tag / GA4 & Google Ads safely
 */
export const initGA = (customGaId, options = {}) => {
  if (typeof window === 'undefined') return;

  const resolved = resolveTrackingCondition(options.context);
  const targetId = customGaId || resolved.googleTag || getGoogleConfig().gaId;
  const adsId = options.adsId || resolved.googleAdsTag;
  const firebaseId = options.firebaseTag || resolved.firebaseTag;

  if (!targetId) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[Google Services] GA4 tracking ID not configured; operating in mock mode.');
    }
    return;
  }

  try {
    const isTest = process.env.NODE_ENV === 'test';

    // Ensure dataLayer and gtag are ready for Tag Assistant extension
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }

    // Preserve exact signature compatibility if customGaId is explicitly passed
    if (customGaId && !options.multi) {
      ReactGA.initialize(customGaId, {
        testMode: isTest,
        ...options.gaOptions,
      });
    } else {
      const initConfigs = [{ trackingId: targetId }];
      if (adsId && adsId !== targetId) {
        initConfigs.push({ trackingId: adsId });
      }
      ReactGA.initialize(initConfigs, {
        testMode: isTest,
        ...options.gaOptions,
      });
    }

    isGaInitialized = true;
    currentGoogleTag = targetId;
    currentCondition = resolved.condition;

    // Direct config calls to ensure Tag Assistant detects all active tags
    if (typeof window.gtag === 'function') {
      window.gtag('config', targetId, {
        send_page_view: false,
        tracking_condition: resolved.condition,
      });
      if (adsId) {
        window.gtag('config', adsId);
      }
      if (firebaseId) {
        window.gtag('config', firebaseId, {
          client_platform: resolved.isMobile ? 'mobile_web' : 'web',
          firebase_project: 'affable-album-250713',
        });
      }
    }

    if (process.env.NODE_ENV !== 'test') {
      console.info(`[Google Services] GA4 initialized successfully with ID: ${targetId} (Ads: ${adsId})`);
    }
  } catch (err) {
    console.warn('[Google Services] Failed to initialize GA4:', err);
  }
};

/**
 * Initialize Google Tag Manager (GTM)
 */
export const initGTM = (customGtmId, options = {}) => {
  if (typeof window === 'undefined') return;

  const resolved = resolveTrackingCondition(options.context);
  const targetGtmId = customGtmId || resolved.gtmTag || getGoogleConfig().gtmId;

  if (!targetGtmId) return;

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

    let script = document.getElementById('gtm-script');
    const gtmSrc = `https://www.googletagmanager.com/gtm.js?id=${targetGtmId}`;

    if (!script) {
      script = document.createElement('script');
      script.id = 'gtm-script';
      script.async = true;
      script.src = gtmSrc;
      script.onerror = () => {
        console.warn(`[Google Services] GTM script failed to load (possibly blocked by client).`);
      };
      document.head.appendChild(script);
    } else if (script.src !== gtmSrc) {
      script.src = gtmSrc;
    }
    isGtmInitialized = true;
    currentGtmTag = targetGtmId;
  } catch (err) {
    console.warn('[Google Services] Failed to initialize GTM:', err);
  }
};

/**
 * Enables safe AdSense push handling by wrapping window.adsbygoogle.push.
 * Prevents TagError ("All 'ins' elements in the DOM with class=adsbygoogle already have ads in them")
 * from bubbling unhandled during fast re-renders, SPA navigations, or duplicate mounts.
 */
export const enableSafeAdsByGoogle = () => {
  if (typeof window === 'undefined') return;

  const wrapPush = (target) => {
    if (!target) return target;
    if (target._safePushWrapped) return target;

    let originalPush = typeof target.push === 'function' ? target.push : Array.prototype.push;

    const safePush = function (...args) {
      try {
        return originalPush.apply(this, args);
      } catch (err) {
        const msg = (err && (err.message || String(err))) || '';
        if (
          msg.includes('already have ads in them') ||
          msg.includes('TagError') ||
          msg.includes('adsbygoogle.push() error') ||
          msg.includes('availableWidth=0')
        ) {
          if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
            console.warn('[SafeAdSense] Suppressed adsbygoogle.push layout/tag warning:', msg);
          }
          return null;
        }
        console.warn('[SafeAdSense] adsbygoogle.push error:', err);
        return null;
      }
    };

    try {
      Object.defineProperty(target, 'push', {
        configurable: true,
        enumerable: true,
        get() {
          return safePush;
        },
        set(newPush) {
          originalPush = typeof newPush === 'function' ? newPush : originalPush;
        },
      });
      target._safePushWrapped = true;
    } catch (e) {
      target.push = safePush;
      target._safePushWrapped = true;
    }

    return target;
  };

  if (window.adsbygoogle) {
    wrapPush(window.adsbygoogle);
  } else {
    window.adsbygoogle = wrapPush([]);
  }

  try {
    let currentAdsByGoogle = window.adsbygoogle;
    Object.defineProperty(window, 'adsbygoogle', {
      configurable: true,
      enumerable: true,
      get() {
        return currentAdsByGoogle;
      },
      set(newVal) {
        currentAdsByGoogle = wrapPush(newVal);
      },
    });
  } catch (e) {
    // If property definition on window is not permitted, window.adsbygoogle is still safely wrapped
  }
};

/**
 * Initialize Google AdSense script safely
 */
export const initAdSense = (customAdsId) => {
  if (typeof window === 'undefined') return;

  enableSafeAdsByGoogle();

  if (isAdSenseInitialized) return;

  const { adsId } = getGoogleConfig();
  const targetAdsId = customAdsId || adsId;

  if (!targetAdsId) return;

  try {
    if (!document.getElementById('adsense-script')) {
      const script = document.createElement('script');
      script.id = 'adsense-script';
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${targetAdsId}`;
      script.onerror = () => {
        console.warn('[Google Services] AdSense script failed to load (likely ad-blocker enabled).');
      };
      document.head.appendChild(script);
    }
    isAdSenseInitialized = true;
  } catch (err) {
    console.warn('[Google Services] Failed to initialize AdSense script:', err);
  }
};

/**
 * Dynamic Tracking Context Update
 * Automatically called on React Router navigation or AuthState transitions
 * to keep Google Tags, GTM, and dataLayer in sync with the active condition.
 */
export const updateTrackingContext = (context = {}) => {
  if (typeof window === 'undefined') return null;

  const resolved = resolveTrackingCondition(context);
  const { condition, googleTag, gtmTag, googleAdsTag, firebaseTag, isMobile } = resolved;

  // If active Google Tag has changed based on new route/user condition
  if (currentGoogleTag !== googleTag) {
    currentGoogleTag = googleTag;
    currentCondition = condition;
    if (typeof window.gtag === 'function') {
      window.gtag('config', googleTag, {
        page_path: context.pathname || window.location.pathname,
        page_search: context.search || window.location.search,
        tracking_condition: condition,
      });
    }
  }

  // If GTM container changed in dynamic runtime
  if (currentGtmTag !== gtmTag) {
    initGTM(gtmTag, { context });
  }

  // Push context event to dataLayer for GTM & Tag Assistant inspection
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'tracking_context_update',
      condition,
      googleTag,
      gtmTag,
      googleAdsTag,
      firebaseTag,
      isMobile,
      timestamp: new Date().toISOString(),
    });
  }

  return resolved;
};

/**
 * Safe Page View Tracking
 * Guards against uninitialized state and ensures page view calls never throw.
 */
export const trackPageView = (path, context = {}) => {
  if (typeof window === 'undefined') return;

  const pagePath = path || (window.location.pathname + window.location.search);
  const resolved = resolveTrackingCondition(context);

  try {
    if (isGaInitialized) {
      ReactGA.send({ hitType: 'pageview', page: pagePath });
    }
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        send_to: resolved.googleTag,
        condition: resolved.condition,
        mobile_browser: resolved.isMobile,
      });
    }
  } catch (err) {
    // Non-fatal, suppress error to prevent breaking user navigation
    console.warn('[Google Services] trackPageView error suppressed:', err);
  }
};

/**
 * Safe Event Tracking
 */
export const trackEvent = (name, params = {}, context = {}) => {
  if (typeof window === 'undefined') return;

  const resolved = resolveTrackingCondition(context);

  try {
    if (isGaInitialized) {
      ReactGA.event(name, params);
    }
    if (window.gtag) {
      window.gtag('event', name, {
        ...params,
        condition: resolved.condition,
      });
    }
  } catch (err) {
    console.warn('[Google Services] trackEvent error suppressed:', err);
  }
};

/**
 * Web Crashlytics / Error Reporting
 * Dispatches unhandled exceptions to GA4/GTM dataLayer, matching Android's
 * FirebaseCrashlytics.getInstance().recordException(err) telemetry.
 */
export const recordException = (error, context = {}, fatal = false) => {
  if (typeof window === 'undefined') return;

  const errorDesc = error?.stack || error?.message || String(error);
  const errorName = error?.name || 'ApplicationError';
  const resolved = resolveTrackingCondition(context);
  const isMobile = isMobileBrowser();

  if (process.env.NODE_ENV !== 'test') {
    console.error(`[Crashlytics Web] ${fatal ? 'FATAL' : 'NON-FATAL'} Exception [${errorName}]:`, errorDesc, context);
  }

  try {
    // Record to dataLayer / gtag with Firebase Crashlytics compatibility
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: errorDesc.substring(0, 150),
        fatal: Boolean(fatal),
        send_to: FIREBASE_TAG,
        device_category: isMobile ? 'mobile_browser' : 'desktop_web',
        ...context,
      });

      if (resolved.googleTag && resolved.googleTag !== FIREBASE_TAG) {
        window.gtag('event', 'exception', {
          description: errorDesc.substring(0, 150),
          fatal: Boolean(fatal),
          send_to: resolved.googleTag,
          ...context,
        });
      }
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'app_exception',
        exceptionType: errorName,
        exceptionMessage: error?.message || String(error),
        fatal: Boolean(fatal),
        firebaseTag: FIREBASE_TAG,
        activeCondition: resolved.condition,
        activeGoogleTag: resolved.googleTag,
        devicePlatform: isMobile ? 'mobile_browser' : 'desktop_web',
        context,
        timestamp: new Date().toISOString(),
      });
    }

    if (isGaInitialized) {
      ReactGA.event('exception', {
        description: errorDesc.substring(0, 100),
        fatal: Boolean(fatal),
      });
    }
  } catch (logErr) {
    // Suppress secondary errors in error reporting
  }
};

/**
 * Crashlytics Log Message
 * Matches FirebaseCrashlytics.getInstance().log(msg)
 */
export const logCrashlytics = (message, level = 'info') => {
  if (typeof window === 'undefined') return;

  try {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'crashlytics_log',
        logLevel: level,
        message,
        firebaseTag: FIREBASE_TAG,
        devicePlatform: isMobileBrowser() ? 'mobile_browser' : 'desktop_web',
        timestamp: new Date().toISOString(),
      });
    }
  } catch (e) {}
};

/**
 * Initialize Global Error Handlers (Unhandled Rejection and Window Error)
 */
export const initGlobalErrorHandlers = () => {
  if (typeof window === 'undefined' || isErrorHandlersInitialized) return;

  window.addEventListener('error', (event) => {
    const message = event?.message || (event?.error && event?.error?.message) || '';
    if (typeof message === 'string' && (
      message.includes('already have ads in them') ||
      message.includes('TagError') ||
      message.includes('adsbygoogle.push() error')
    )) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return;
    }
    recordException(
      event.error || event.message,
      { source: 'window.onerror', lineno: event.lineno, colno: event.colno },
      false
    );
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reasonMsg = (event?.reason && (event.reason.message || String(event.reason))) || '';
    if (typeof reasonMsg === 'string' && (
      reasonMsg.includes('already have ads in them') ||
      reasonMsg.includes('TagError') ||
      reasonMsg.includes('adsbygoogle.push() error')
    )) {
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      return;
    }
    recordException(event.reason, { source: 'unhandledrejection' }, false);
  });

  isErrorHandlersInitialized = true;
};

/**
 * Master initializer for all Google Services
 */
export const initGoogleServices = (context = {}) => {
  if (typeof window === 'undefined') return;

  // Initialize dataLayer, gtag, and safe AdSense push early
  enableSafeAdsByGoogle();

  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
  }

  const resolved = resolveTrackingCondition(context);
  const config = getGoogleConfig(context);

  if (config.shouldInit) {
    initGA(resolved.googleTag, {
      adsId: resolved.googleAdsTag,
      firebaseTag: FIREBASE_TAG,
      context,
      multi: true,
    });
    initGTM(resolved.gtmTag, { context });
    if (config.adsId) initAdSense(config.adsId);
  }
  initGlobalErrorHandlers();
};

export const getCurrentCondition = () => currentCondition;
export const isGtmReady = () => isGtmInitialized;

const googleServices = {
  TAG_CONDITIONS,
  FIREBASE_TAG,
  GOOGLE_ADS_TAG,
  isMobileBrowser,
  resolveTrackingCondition,
  updateTrackingContext,
  initGoogleServices,
  initGA,
  initGTM,
  initAdSense,
  enableSafeAdsByGoogle,
  getCurrentCondition,
  trackPageView,
  trackEvent,
  recordException,
  logCrashlytics,
  initGlobalErrorHandlers,
  getGoogleConfig,
};

export default googleServices;

